import {
  useEffect,
  useMemo,
  useState,
} from "react"
import {
  CellContext,
  ColumnDef,
  SortDirection,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import styles from 'components/trafficSummaryModal/trafficSummaryModal.module.scss'
import {
  ariaSort,
  findByKey,
  findParamValue,
  formatNumber,
  norwegianTextSort,
} from "components/trafficTable/trafficTableHelpers"
import { NoiseSourceResponse_Scenario } from "services/sicalcApi/responses/noiseSourceResponse"
import { CustomScenarioResponse } from "services/sicalcApi/responses/customScenarioResponse"
import { TrafficParamNameResponse } from "services/sicalcApi/responses/trafficParamNameListResponse"
import {
  ScenarioTrafficSummaryResponse,
  TrafficSummaryRowResponse,
} from "services/sicalcApi/responses/trafficSummaryListResponse"
import { useSicalcApiService } from "services/sicalcApi/sicalcApiService"
import { ModalBase } from "components/common/modals/modalBase/modalBase"
import { ModalProps } from "services/modal/modalService"
import { LightButton } from "components/common/buttons/lightButton/lightButton"

export interface TrafficSummaryModalProps extends ModalProps {
  calculationTaskId: string
  scenarios: NoiseSourceResponse_Scenario[] | undefined
  customScenarios: CustomScenarioResponse[] | undefined
  /** The scenarios that are part of the calculation task, shown by default */
  initialScenarioIds?: string[]
}

interface SelectableScenario {
  id: string
  label: string
  isCustom: boolean
}

interface OperationColumn {
  id: OperationColumnId
  displayName: string
}

type OperationColumnId = keyof ScenarioTrafficSummaryResponse

const operationColumns: OperationColumn[] = [
  {
    id: 'operations',
    displayName: 'Totalt',
  },
  {
    id: 'operD',
    displayName: 'Dag',
  },
  {
    id: 'operE',
    displayName: 'Kveld',
  },
  {
    id: 'operN',
    displayName: 'Natt',
  },
]

const emptyTraffic: ScenarioTrafficSummaryResponse = {
  operations: 0,
  operD: 0,
  operE: 0,
  operN: 0,
}

/**
 * Operation columns are identified by the scenario they belong to and the
 * operation column itself, so the same operation can be sorted independently
 * per scenario.
 */
const operationColumnId = (scenarioId: string, columnId: OperationColumnId) => `${scenarioId}|${columnId}`

const isOperationColumnId = (columnId: string) => columnId.includes('|')

/** True for the operation columns that are hidden when a scenario is collapsed */
const isHiddenWhenCollapsed = (columnId: string, scenarioId: string) =>
  columnId.startsWith(`${scenarioId}|`) && columnId !== operationColumnId(scenarioId, 'operations')

const findScenarioTraffic = (row: TrafficSummaryRowResponse, scenarioId: string) =>
  findByKey(row.scenariosTraffic, scenarioId) ?? emptyTraffic

export function TrafficSummaryModal(props: TrafficSummaryModalProps) {
  const { trafficApi } = useSicalcApiService()

  const [ paramNames, setParamNames ] = useState<TrafficParamNameResponse[]>([])
  const [ selectedParamKeys, setSelectedParamKeys ] = useState<string[]>([])
  const [ selectedScenarioIds, setSelectedScenarioIds ] = useState<string[]>([])
  const [ rows, setRows ] = useState<TrafficSummaryRowResponse[]>([])
  const [ sorting, setSorting ] = useState<SortingState>([])
  // Tracked as expanded rather than collapsed so scenarios start out, and stay,
  // collapsed to their total until the user asks for the breakdown
  const [ expandedScenarioIds, setExpandedScenarioIds ] = useState<Set<string>>(new Set())
  const [ isLoading, setIsLoading ] = useState(false)
  const [ errorMessage, setErrorMessage ] = useState<string>()

  const selectableScenarios = useMemo<SelectableScenario[]>(() => [
    ...(props.scenarios ?? []).map(scenario => ({
      id: scenario.id,
      label: `${scenario.targetYear} – ${scenario.name}`,
      isCustom: false,
    })),
    ...(props.customScenarios ?? []).map(customScenario => ({
      id: customScenario.id,
      label: `${customScenario.targetYear} – ${customScenario.name}`,
      isCustom: true,
    })),
  ], [ props.scenarios, props.customScenarios ])

  // Start out showing the scenarios that are part of the calculation task, or
  // the first one if none are, so the table is not empty on open
  useEffect(() => {
    setSelectedScenarioIds(currentIds => {
      if (currentIds.length > 0) {
        return currentIds
      }

      const initialIds = selectableScenarios
        .filter(s => props.initialScenarioIds?.includes(s.id))
        .map(s => s.id)

      return initialIds.length > 0
        ? initialIds
        : selectableScenarios.slice(0, 1).map(s => s.id)
    })
  }, [ selectableScenarios, props.initialScenarioIds ])

  // Load the parameters that the traffic can be grouped by, and start out
  // grouped by the first one
  useEffect(() => {
    let cancelled = false

    trafficApi.getParamNames()
      .then(response => {
        if (cancelled) {
          return
        }
        setParamNames(response.paramNames)
        setSelectedParamKeys(response.paramNames.slice(0, 1).map(p => p.key))
      })
      .catch(() => {
        if (!cancelled) {
          setErrorMessage("Kunne ikke hente hvilke kolonner som er tilgjengelige")
        }
      })

    return () => {
      cancelled = true
    }
  }, [ trafficApi ])

  const selectedScenarios = useMemo(
    () => selectableScenarios.filter(s => selectedScenarioIds.includes(s.id)),
    [ selectableScenarios, selectedScenarioIds ])

  useEffect(() => {
    let cancelled = false

    if (selectedScenarios.length === 0 || selectedParamKeys.length === 0) {
      setRows([])
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setErrorMessage(undefined)

    // scenarioIds and isCustomScenario must be index aligned, so both are
    // derived from the same list
    trafficApi.getSummary({
      calculationTaskId: props.calculationTaskId,
      scenarioIds: selectedScenarios.map(s => s.id),
      isCustomScenario: selectedScenarios.map(s => s.isCustom),
      paramKeys: selectedParamKeys,
    })
      .then(response => {
        if (!cancelled) {
          setRows(response.summaryRows)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRows([])
          setErrorMessage("Kunne ikke hente trafikk for scenariene")
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [ trafficApi, props.calculationTaskId, selectedScenarios, selectedParamKeys ])

  const selectedParams = useMemo(
    () => paramNames.filter(p => selectedParamKeys.includes(p.key)),
    [ paramNames, selectedParamKeys ])

  // One column per grouping parameter, then a group of operation columns per
  // scenario. The scenario groups become the top header row.
  const columns = useMemo<ColumnDef<TrafficSummaryRowResponse, unknown>[]>(() => [
    ...selectedParams.map(param => ({
      id: param.key,
      header: param.displayName,
      accessorFn: (row: TrafficSummaryRowResponse) => findParamValue(row, param.key),
      sortingFn: norwegianTextSort<TrafficSummaryRowResponse>(),
    })),
    ...selectedScenarios.map(scenario => ({
      id: scenario.id,
      header: scenario.label,
      columns: operationColumns.map(column => ({
        id: operationColumnId(scenario.id, column.id),
        header: column.displayName,
        accessorFn: (row: TrafficSummaryRowResponse) => findScenarioTraffic(row, scenario.id)[column.id],
        cell: (info: CellContext<TrafficSummaryRowResponse, unknown>) => formatNumber(info.getValue<number>()),
      })),
    })),
  ], [ selectedParams, selectedScenarios ])

  // A collapsed scenario shows only its total, so the rest of its operation
  // columns are hidden. The scenario's header colSpan follows automatically.
  const columnVisibility = useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {}

    for (const scenario of selectedScenarios) {
      if (expandedScenarioIds.has(scenario.id)) {
        continue
      }
      for (const column of operationColumns) {
        if (column.id !== 'operations') {
          visibility[operationColumnId(scenario.id, column.id)] = false
        }
      }
    }

    return visibility
  }, [ selectedScenarios, expandedScenarioIds ])

  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnVisibility,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  // The first operation column of each scenario carries the separating border
  const groupStartColumnIds = useMemo(
    () => new Set(selectedScenarios.map(s => operationColumnId(s.id, operationColumns[0].id))),
    [ selectedScenarios ])

  const totals = useMemo(() => {
    const totalsByScenario: { [scenarioId: string]: ScenarioTrafficSummaryResponse } = {}

    for (const scenario of selectedScenarios) {
      totalsByScenario[scenario.id] = rows.reduce((accumulated, row) => {
        const traffic = findScenarioTraffic(row, scenario.id)
        return {
          operations: accumulated.operations + traffic.operations,
          operD: accumulated.operD + traffic.operD,
          operE: accumulated.operE + traffic.operE,
          operN: accumulated.operN + traffic.operN,
        }
      }, { ...emptyTraffic })
    }

    return totalsByScenario
  }, [ rows, selectedScenarios ])

  const toggleColumn = (paramKey: string) => {
    setSelectedParamKeys(currentKeys => {
      const nextKeys = currentKeys.includes(paramKey)
        ? currentKeys.filter(key => key !== paramKey)
        : [ ...currentKeys, paramKey ]

      // Keep the column order aligned with the order the server reports them in
      return paramNames.filter(p => nextKeys.includes(p.key)).map(p => p.key)
    })
    setSorting([])
  }

  const toggleScenario = (scenarioId: string) => {
    setSelectedScenarioIds(currentIds => {
      const nextIds = currentIds.includes(scenarioId)
        ? currentIds.filter(id => id !== scenarioId)
        : [ ...currentIds, scenarioId ]

      // Keep the scenario order stable regardless of the order they were picked
      return selectableScenarios.filter(s => nextIds.includes(s.id)).map(s => s.id)
    })
    setSorting([])
  }

  const toggleScenarioExpanded = (scenarioId: string) => {
    const willCollapse = expandedScenarioIds.has(scenarioId)

    const nextIds = new Set(expandedScenarioIds)
    if (willCollapse) {
      nextIds.delete(scenarioId)
    } else {
      nextIds.add(scenarioId)
    }
    setExpandedScenarioIds(nextIds)

    if (willCollapse) {
      // Don't keep sorting by a column that is about to be hidden
      setSorting(currentSorting => currentSorting.filter(s => !isHiddenWhenCollapsed(s.id, scenarioId)))
    }
  }

  const sortIndicator = (sortDirection: SortDirection | false) => {
    if (sortDirection === false) {
      return null
    }
    return (
      <i className={`mdi mdi-arrow-${sortDirection === 'asc' ? 'up' : 'down'} ${styles['sort-indicator']}`}></i>
    )
  }

  const dismiss = () => {
    props.modalContext.dismiss()
  }

  const columnCount = table.getVisibleLeafColumns().length
  const headerGroups = table.getHeaderGroups()
  // Without a selected scenario there is only one header row, so nothing to span
  const hasScenarioHeaderRow = headerGroups.length > 1
  const tableRows = table.getRowModel().rows
  const hasSelection = selectedScenarios.length > 0 && selectedParamKeys.length > 0

  const tableMessage = () => {
    if (selectedScenarios.length === 0) {
      return "Velg minst ett scenario"
    }
    if (selectedParamKeys.length === 0) {
      return "Velg minst én kolonne"
    }
    // While reloading, the previous rows stay on screen (dimmed)
    if (tableRows.length === 0) {
      return isLoading ? "Henter trafikk ..." : "Ingen trafikkdata for de valgte scenariene"
    }
    return undefined
  }

  return (
    <ModalBase
      onDismiss={dismiss}
      heading="Trafikk"
      actions={
        <LightButton onClick={dismiss}>
          Lukk
        </LightButton>
      }
    >
      <div className={`${styles.body} column`}>
        <div className={'column'}>
          <span className={'form-label'}>Scenarier</span>
          <div className={`${styles['option-list']} row wrap`}>
            {selectableScenarios.map(scenario => (
              <label className={`${styles.option} row align-center`} key={scenario.id}>
                <input
                  type="checkbox"
                  checked={selectedScenarioIds.includes(scenario.id)}
                  onChange={() => toggleScenario(scenario.id)}
                />
                <span>{scenario.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className={'column'}>
          <span className={'form-label'}>Kolonner</span>
          <div className={`${styles['option-list']} row wrap`}>
            {paramNames.map(paramName => (
              <label className={`${styles.option} row align-center`} key={paramName.key}>
                <input
                  type="checkbox"
                  checked={selectedParamKeys.includes(paramName.key)}
                  onChange={() => toggleColumn(paramName.key)}
                />
                <span>{paramName.displayName}</span>
              </label>
            ))}
          </div>
        </div>

        {errorMessage !== undefined && (
          <div className={'validation-error'}>
            <i className="mdi mdi-alert-circle-outline"></i>
            <span className="ml2">{errorMessage}</span>
          </div>
        )}

        <div className={`${styles['table-container']} ${isLoading ? styles['is-loading'] : ''}`}>
          <table className={styles.table}>
            <thead>
              {headerGroups.map((headerGroup, headerGroupIndex) => (
                <tr
                  className={headerGroupIndex === 0 ? styles['header-row-top'] : styles['header-row-bottom']}
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map(header => {
                    // Ask the column whether it has children: a placeholder header
                    // also has subHeaders, so subHeaders can't tell them apart
                    const isScenarioGroup = header.column.columns.length > 0
                    const isParameterColumn = !isScenarioGroup && !isOperationColumnId(header.column.id)

                    // Parameter columns sit under no scenario, so they are rendered
                    // once in the top row and span both header rows. TanStack emits
                    // a placeholder for them in one row and the real header in the
                    // other, so pick the row by column rather than by placeholder.
                    if (isParameterColumn && headerGroupIndex !== 0) {
                      return null
                    }

                    const isCollapsed = !expandedScenarioIds.has(header.column.id)
                    const startsScenarioGroup = isScenarioGroup
                      || groupStartColumnIds.has(header.column.id)

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan > 1 ? header.colSpan : undefined}
                        rowSpan={isParameterColumn && hasScenarioHeaderRow ? 2 : undefined}
                        className={`${isScenarioGroup ? styles['scenario-header'] : ''} `
                          + `${!isScenarioGroup && isOperationColumnId(header.column.id) ? styles.numeric : ''} `
                          + `${startsScenarioGroup ? styles['scenario-group'] : ''}`}
                        aria-sort={isScenarioGroup ? undefined : ariaSort(header.column.getIsSorted())}
                        aria-expanded={isScenarioGroup ? !isCollapsed : undefined}
                        title={isScenarioGroup
                          ? (isCollapsed ? "Vis alle kolonner" : "Vis bare totalt")
                          : undefined}
                        onClick={isScenarioGroup
                          ? () => toggleScenarioExpanded(header.column.id)
                          : header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isScenarioGroup && (
                          <i
                            className={`mdi mdi-arrow-${isCollapsed ? 'expand' : 'collapse'}-horizontal `
                              + `${styles['collapse-indicator']}`}
                          ></i>
                        )}
                        {!isScenarioGroup && sortIndicator(header.column.getIsSorted())}
                      </th>
                    )
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {tableMessage() !== undefined && (
                <tr>
                  <td className={styles['table-message']} colSpan={Math.max(columnCount, 1)}>
                    {tableMessage()}
                  </td>
                </tr>
              )}
              {hasSelection && tableRows.map(row => (
                <tr key={row.id}>
                  {row.getVisibleCells().map(cell => (
                    <td
                      key={cell.id}
                      className={`${isOperationColumnId(cell.column.id) ? styles.numeric : ''} `
                        + `${groupStartColumnIds.has(cell.column.id) ? styles['scenario-group'] : ''}`}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            {hasSelection && tableRows.length > 0 && (
              <tfoot>
                <tr>
                  {/* Derived from the visible columns so it follows collapsed scenarios */}
                  {table.getVisibleLeafColumns().map((column, index) => {
                    if (!isOperationColumnId(column.id)) {
                      return <td key={column.id}>{index === 0 ? 'Sum' : ''}</td>
                    }

                    const [ scenarioId, operationColumn ] = column.id.split('|')

                    return (
                      <td
                        className={`${styles.numeric} `
                          + `${groupStartColumnIds.has(column.id) ? styles['scenario-group'] : ''}`}
                        key={column.id}
                      >
                        {formatNumber((totals[scenarioId] ?? emptyTraffic)[operationColumn as OperationColumnId])}
                      </td>
                    )
                  })}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </ModalBase>
  )
}
